import React, { useRef, useState } from "react";
import { UploadCloud, X, GripVertical, Play } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";

export default function MediaUploader({ value = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef();
  const dragIdx = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      onChange([...value, ...data.files]);
      toast.success(`${data.files.length} file(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from == null || from === i) return;
    const copy = [...value];
    const [moved] = copy.splice(from, 1);
    copy.splice(i, 0, moved);
    onChange(copy);
    dragIdx.current = null;
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-[#333] rounded-xl p-6 text-center cursor-pointer hover:border-[#E60000] transition-colors"
        data-testid="media-dropzone"
      >
        <UploadCloud size={30} className="mx-auto text-[#E60000] mb-2" />
        <p className="text-[#F0EDEA] text-sm font-semibold">Tap to upload photos & videos</p>
        <p className="text-[#A8A2A0] text-xs mt-1">JPG, PNG, HEIC, WEBP, MP4, MOV — multiple at once</p>
        <input
          ref={inputRef} type="file" multiple accept="image/*,video/*,.heic,.heif,.mov"
          className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="media-input"
        />
      </div>

      {uploading && (
        <div className="mt-3" data-testid="upload-progress">
          <div className="h-2 bg-[#1C1C1C] rounded-full overflow-hidden">
            <div className="h-full bg-[#E60000] transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[#A8A2A0] text-xs mt-1">Uploading... {progress}%</p>
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="media-preview-grid">
          {value.map((m, i) => (
            <div
              key={m.path || i}
              draggable
              onDragStart={() => (dragIdx.current = i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="relative aspect-square rounded-lg overflow-hidden border border-[#333] bg-black group"
              data-testid={`media-item-${i}`}
            >
              {m.kind === "video"
                ? <><video src={mediaUrl(m)} className="w-full h-full object-cover" muted /><Play size={20} className="absolute inset-0 m-auto text-white/80" /></>
                : <img src={mediaUrl(m)} className="w-full h-full object-cover" alt="" />}
              {i === 0 && <span className="absolute top-1 left-1 bg-[#E60000] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">COVER</span>}
              <GripVertical size={14} className="absolute bottom-1 left-1 text-white/60" />
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1" data-testid={`media-remove-${i}`}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
